// Heal-on-mount: walks all deallink__ docs the user participates in, then for
// each Client (or Sub) row that is missing `linkedUid`, checks whether the
// deallink's OTHER party email/name matches. If so, stamps `linkedUid` on the
// local row so downstream hooks (useHealMissingPortalLinks, portalSync,
// useEstimateMutations, useProjectMutations) can find the client's uid without
// an `as unknown as` cast.
//
// Why this exists: the link-approval flow (Clients.tsx onApprove) stamps
// `linkedUid` on newly created Client/Sub rows as of the bug-fix commit.
// But Client rows created BEFORE that fix (Tom's existing TJN Remodeling LLC /
// TE Group Homes etc.) have no `linkedUid`. Without it, the receiver's
// `array-contains` query on participantUids finds nothing, so the portal link
// is invisible on the other side.
//
// This hook runs ONCE per session, BEFORE useHealMissingPortalLinks, so that
// when the portal heal reads `client.linkedUid` it finds the backfilled value.
//
// Idempotent: rows already carrying `linkedUid` are skipped.
// Throttled: 150 ms between writes (same cadence as useHealMissingPortalLinks).

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { qk } from './queryKeys'
import { listMyDealLinks } from '../lib/db/shared'
import { saveSlice, saveMisc } from '../lib/db'
import type { AppState } from '../types/firestore'
import type { Client, Sub } from '../types'

const healed = new Set<string>()

export function useHealClientLinkedUids(): void {
  const { effectiveUid: uid, isReady } = useAuth()
  const qc = useQueryClient()
  const ranRef = useRef(false)

  useEffect(() => {
    if (!uid || !isReady) return
    if (healed.has(uid)) return

    let retries = 0
    const maxRetries = 20 // 10s total @ 500 ms

    const tick = async () => {
      const state = qc.getQueryData<AppState>(qk.appstate(uid))
      if (!state) {
        if (retries++ < maxRetries) setTimeout(tick, 500)
        return
      }
      if (healed.has(uid)) return
      healed.add(uid)

      // Fetch all deallinks we participate in (single Firestore query).
      let deallinks: Awaited<ReturnType<typeof listMyDealLinks>>
      try {
        deallinks = await listMyDealLinks(uid)
      } catch (err) {
        console.warn('[heal-linkedUid] could not fetch deallinks', err)
        return
      }

      if (deallinks.length === 0) return

      // Build a lookup: otherUid → { email?, name? } from each deallink.
      // Use a record type with required-but-possibly-undefined to satisfy
      // exactOptionalPropertyTypes (we build via conditional spread).
      const otherParties: Array<{ uid: string; email?: string; name?: string }> = []
      for (const d of deallinks) {
        if (d.status !== 'active') continue
        const raw = d as unknown as {
          initiatorUid?: string; partnerUid?: string;
          initiatorEmail?: string; partnerEmail?: string;
          initiatorName?: string; partnerName?: string;
        }
        if (!raw.initiatorUid || !raw.partnerUid) continue
        const otherUid   = raw.initiatorUid === uid ? raw.partnerUid   : raw.initiatorUid
        const otherEmail = (raw.initiatorUid === uid ? raw.partnerEmail : raw.initiatorEmail)?.toLowerCase().trim()
        const otherName  = (raw.initiatorUid === uid ? raw.partnerName  : raw.initiatorName)?.toLowerCase().trim()
        if (otherUid) {
          otherParties.push({
            uid: otherUid,
            ...(otherEmail !== undefined ? { email: otherEmail } : {}),
            ...(otherName  !== undefined ? { name:  otherName  } : {}),
          })
        }
      }

      if (otherParties.length === 0) return

      // --- Heal clients ---
      const latestState = qc.getQueryData<AppState>(qk.appstate(uid)) ?? state
      const patchedClients: Client[] = [...latestState.clients]
      let clientDirty = false

      for (let i = 0; i < patchedClients.length; i++) {
        const c = patchedClients[i]!
        if (c.linkedUid) continue // already stamped

        const emailLC = c.email?.toLowerCase().trim()
        const nameLC  = c.name?.toLowerCase().trim()

        for (const other of otherParties) {
          const emailMatch = !!emailLC && !!other.email && emailLC === other.email
          const nameMatch  = !!nameLC  && !!other.name  && nameLC  === other.name
          if (!emailMatch && !nameMatch) continue

          // Warn if multiple clients match the same deallink (ambiguous).
          const dupeCount = patchedClients.filter((x) => {
            const xEmail = x.email?.toLowerCase().trim()
            const xName  = x.name?.toLowerCase().trim()
            return (!x.linkedUid) && (
              (!!xEmail && !!other.email && xEmail === other.email) ||
              (!!xName  && !!other.name  && xName  === other.name)
            )
          }).length
          if (dupeCount > 1) {
            console.warn(
              '[heal-linkedUid] multiple Client rows match deallink other-party', other.uid,
              '-- stamping all matches; review manually if unexpected',
            )
          }

          console.warn(
            '[heal-linkedUid] stamping linkedUid on client', c.id, c.name, '->', other.uid,
          )
          patchedClients[i] = { ...c, linkedUid: other.uid }
          clientDirty = true
          break
        }
        await new Promise((r) => setTimeout(r, 150))
      }

      if (clientDirty) {
        try {
          await saveSlice(uid, 'clients', patchedClients)
          qc.setQueryData(qk.appstate(uid), {
            ...(qc.getQueryData<AppState>(qk.appstate(uid)) ?? latestState),
            clients: patchedClients,
          })
          const count = patchedClients.filter((c, i) => c.linkedUid && !latestState.clients[i]?.linkedUid).length
          console.warn('[heal-linkedUid] stamped linkedUid on', count, 'client row(s)')
        } catch (err) {
          console.warn('[heal-linkedUid] saveSlice clients failed', err)
        }
      }

      // --- Heal subs ---
      const latestState2 = qc.getQueryData<AppState>(qk.appstate(uid)) ?? latestState
      const patchedSubs: Sub[] = [...latestState2.subs]
      let subDirty = false

      for (let i = 0; i < patchedSubs.length; i++) {
        const s = patchedSubs[i]!
        if (s.linkedUid) continue

        const emailLC = s.email?.toLowerCase().trim()
        const nameLC  = s.name?.toLowerCase().trim()

        for (const other of otherParties) {
          const emailMatch = !!emailLC && !!other.email && emailLC === other.email
          const nameMatch  = !!nameLC  && !!other.name  && nameLC  === other.name
          if (!emailMatch && !nameMatch) continue

          console.warn(
            '[heal-linkedUid] stamping linkedUid on sub', s.id, s.name, '->', other.uid,
          )
          patchedSubs[i] = { ...s, linkedUid: other.uid }
          subDirty = true
          break
        }
        await new Promise((r) => setTimeout(r, 150))
      }

      if (subDirty) {
        try {
          await saveMisc(uid, { subs: patchedSubs })
          qc.setQueryData(qk.appstate(uid), {
            ...(qc.getQueryData<AppState>(qk.appstate(uid)) ?? latestState2),
            subs: patchedSubs,
          })
          const count = patchedSubs.filter((s, i) => s.linkedUid && !latestState2.subs[i]?.linkedUid).length
          console.warn('[heal-linkedUid] stamped linkedUid on', count, 'sub row(s)')
        } catch (err) {
          console.warn('[heal-linkedUid] saveMisc subs failed', err)
        }
      }
    }

    void tick()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isReady, qc])

  // Silence unused-ref lint warning
  void ranRef
}
